import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ObjectTable } from './ObjectTable';
import { resourceGetQueryList } from '../redux/ResourceFetcher';
import { ElementMover } from '../utils/Sorters';
import { useFormFullSelector, useFormSelector } from '../redux/ReduxHelper';
import { addQueryResourceAction, deleteResourceRaw } from '../redux/actions/resource';
import { daoPost } from '../Dao';
import { Spinner } from 'styles/components/Spinner';
import Pagination from './Pagination';
import SearchField from './SearchField';
import { generateHtmlId } from 'mrf/utils/StringGenerators';
import If from '../utils/If';
import usePrintMode from 'mrf/utils/usePrintMode';
import { useTranslation } from 'react-i18next';
import Flex from 'mrf/layout/Flex';
import notifyError from 'mrf/utils/notification';

const defaultPageSize = 20;

QueryObjectTable.propTypes = {
    resource: PropTypes.string.isRequired, // Resource name to save/retrieve data on redux.
    extraQueryParams: PropTypes.object, // Extra GET parameters for Query endpoint.
    endpoint: PropTypes.string, // Query endpoint.
    moveEndpoint: PropTypes.string, // Move endpoint.
    descriptor: PropTypes.object,
    className: PropTypes.string, // Surrounding DIV class.
    pageSize: PropTypes.number,
    queryEndpoint: PropTypes.string,
    movable: PropTypes.bool,
    hideSearchField: PropTypes.bool,
    dependencyList: PropTypes.array,
    loadingMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    debug: PropTypes.bool,
    disableFetch: PropTypes.bool,
};

export default function QueryObjectTable({
    resource,
    endpoint=`/api/${resource}`,
    queryEndpoint=`${endpoint}/query`,
    moveEndpoint=`/api/${resource}/move`,
    movable=false,
    className,
    descriptor,
    extraQueryParams,
    hideSearchField,
    dependencyList=[],
    loadingMessage='Loading...',
    errorMessage='Unexpected error occurred...',
    pageSize,
    debug=false,
    disableFetch=false,
    ...props
}) {
    const dispatch = useDispatch();

    const { t } = useTranslation();

    const printMode = usePrintMode();

    const resourceById = useSelector(state => state.resources[resource]?.byId);
    const resourceList = useSelector(state => state.resources[resource + 'Query']?.list);

    const [bufferedTimeout, setBufferedTimeout] = useState();
    const [messageOverride, setMessageOverride] = useState(loadingMessage);

    const context = useFormSelector({
        resource: resource + 'Query',
        blankForm: () => ({
            fetch: true,
            page: 0,
            pageSize: pageSize ?? defaultPageSize,
            pageCount: -1,
            rowCount: -1,
        }),
        blankCondition: () => !disableFetch,
    });

    const { data: form, set: setFormValue } = useFormFullSelector(context);

    const oldFormData = useRef();

    useEffect(() => {
        return () => {
            dispatch(deleteResourceRaw(resource + 'Query'));
        }
    }, []); // eslint-disable-line

    const fetchTable = async () => {
        if (disableFetch) {
            setMessageOverride(undefined);
            return;
        }

        setMessageOverride(loadingMessage)
        clearTimeout(bufferedTimeout);

        const query = {
            ...(form?.queryString ? { query: form?.queryString } : {}),
            page: form?.page ?? 0,
            pageSize: form?.pageSize ?? defaultPageSize,
            ...extraQueryParams,
        };

        const mapPagedResult = pr => {
            setFormValue({
                fetch: false,
                pageCount: pr?.pageCount,
                rowCount: pr?.rowCount,
            });

            if (!pr?.results) {
                console.error(`This endpoint apparently doesn't support pagination: ${queryEndpoint}`);
            }

            return pr?.results;
        }

        try {
            await resourceGetQueryList({ dispatch, resource, endpoint: queryEndpoint, descriptor, query, outerMap: mapPagedResult });
            setMessageOverride(undefined);
        } catch (e) {
            setMessageOverride(errorMessage);
            if (e?.status === 403) {
                setMessageOverride(e?.roleCodes ?
                    t('To access this information you need the roles: "{{ roles }}"', { roles: e?.roleCodes?.join('", "') })
                    : 'You don\'t have permission to access this information'
                );
            }
            throw e;
        }
    }

    useEffect(() => {
        // If just fast fetched, ignore this request.
        if (oldFormData.current?.fetch === true && form?.fetch === false) {
            return;
        }

        // Fast fetching when requested to do so.
        if (form?.fetch) {
            fetchTable();
            return;
        }

        // Set page to 0 if page size has changed.
        if (oldFormData.current?.pageSize !== form?.pageSize) {
            setFormValue({ fetch: true, page: 0 });
            return;
        }

        // Set page to 0 if query has changed.
        if (oldFormData.current?.queryString !== form?.queryString) {
            setFormValue({ fetch: true, page: 0 });
            return;
        }

        const timeout = setTimeout(fetchTable, 500);
        setBufferedTimeout(timeout);
        return () => clearTimeout(timeout);
    }, [ form?.fetch, form?.queryString, form?.page, form?.pageSize, extraQueryParams, ...dependencyList ]); // eslint-disable-line

    useEffect(() => {
        oldFormData.current = form;
    });

    if (!form) {
        return <Spinner />;
    }

    const moveElement = async (idxSource, idxDestination) => {
        if (resourceList) {
            const data = resourceList.map(id => ({ ...resourceById[id] }));

            if (moveEndpoint) {
                try {
                    await daoPost(moveEndpoint, { source: data[idxSource].sequence, destination: data[idxDestination].sequence });
                } catch (e) {
                    notifyError("Unable to reorder the table.", e);
                }

            }

            dispatch(addQueryResourceAction(resource, ElementMover(data, idxSource, idxDestination)));
        }
    }

    const handleKeyPress = ({ keyCode }) => {
        if (keyCode === 13) {
            fetchTable();
        }
    }

    return (
        <Flex direction='col' className={className}>
            <If condition={!hideSearchField && !printMode}>
                <SearchField
                    placeholder='Search'
                    field='queryString'
                    context={context}
                    onKeyDown={handleKeyPress}
                    handleSearch={fetchTable}
                    id={generateHtmlId(resource, 'search')}
                />
            </If>
            <Flex direction='col' gap={0}>
                <ObjectTable
                    byId={resourceById}
                    list={resourceList}
                    moveElementCb={moveElement}
                    movable={movable}
                    descriptor={descriptor}
                    footer
                    htmlId={generateHtmlId('table', resource)}
                    messageOverride={!printMode && messageOverride}
                    debug={debug}
                    {...props}
                />
                <If condition={!printMode}>
                    <Pagination context={context} />
                </If>
            </Flex>
        </Flex>
    );
}
